/**
 * @module acng/messenger/factory/dialog
 */
const ngFactoryName = 'Dialog';
export default ngFactoryName;
export const DialogFactory = [
  'Amateur',
  'sock',
  'user',
  'http',
  'price',
  'logger',
  'onsLaforgeEvents',
  factory,
];

import {Amateur, AmateurConstructor} from 'acng/amateurPool/factory/Amateur';
import {Message, MessageData} from '../model/message';
import {User} from 'acng/userPool/factory/user';
import {EventTarget, trigger} from '@acng/frontend-bounty';
import {Attachment} from '../factory/attachmentList';
import {setGlobalDialog} from '../context/global-dialog.js';

export type DialogConstructor = new (id: string) => Dialog;

export interface Dialog extends EventTarget {
  id: string;
  lastActivity: Date;
  new: boolean;
  activated: number;
  open: boolean;
  getMessages(): Promise<Message[]>;
  getLastMessage(): Promise<Message>;
  sendMessage(message: string, attachment?: Attachment): Promise<void>;
  read(): void;
  show(): void;
}

const AMATEUR = Symbol();
const DRAFT_TEXT = Symbol();
const DRAFT_ATTACHMENT = Symbol();

function factory(
  Amateur: AmateurConstructor, //
  sock: core.Sock,
  user: User,
  http: core.Http,
  price: core.price,
  logger: any,
  onsLaforgeEvents: any
) {
  return class extends EventTarget implements Dialog {
    id;
    lastActivity = new Date(0);
    new = false;
    activated = 0;
    open = false;
    events = {};
    [AMATEUR]?: Amateur;
    [DRAFT_TEXT] = '';
    [DRAFT_ATTACHMENT]: Attachment | null = null;

    constructor(id: string) {
      super();
      this.id = id;
    }

    get active() {
      if (!!this.activated && this.lastActivity.getTime() < Date.now() - 1000 * 60 * 15) {
        this.activated = 0;
      }
      return !!this.activated;
    }

    setDraftText(text: string) {
      this[DRAFT_TEXT] = text;
      trigger(this, 'drafttext', undefined);
    }

    setDraftAttachment(attachment: Attachment | null) {
      this[DRAFT_ATTACHMENT] = attachment;
      trigger(this, 'draftattachment', undefined);
    }

    /**
     * Get the most recent message
     */
    async getLastMessage() {
      if (!sock.online()) {
        throw new Error('Dialog: Socket offline');
      }
      const amateur = (this[AMATEUR] ??= await Amateur.get(this.id));
      const messages = (await sock.send('getDialog', {
        partner: this.id, // this is complete
        limit: 1,
      })) as MessageData[]; // TODO sock types
      const data = messages.pop();
      if (!data) {
        throw new Error(`Dialog (${this.id}): No Message found`);
      }
      const msg = new Message(amateur, data);
      this.lastActivity = msg.time;
      return msg;
    }

    /**
     * Get the most recent twenty messages.
     */
    async getMessages() {
      const amateur = (this[AMATEUR] ??= await Amateur.get(this.id));
      const dataArray = (await sock.send('getDialog', {
        partner: this.id,
        limit: 20,
      })) as MessageData[]; // TODO sock types
      return dataArray.map((data) => new Message(amateur, data));
    }

    /**
     * Publish dialog.read command to the EventBus
     */
    read() {
      sock.publish('dialog.read', this.id);
    }

    /**
     * Make a frontend broadcast and hopefully the dialog will appear somewhere to the user
     */
    show() {
      setGlobalDialog(this.id);
    }

    /** Publish dialog.close command to the EventBus */
    close() {
      sock.publish('dialog.close', this.id);
    }

    /**
     * Send a message into the Dialog and notify "LaForge" if it is an "icebreaker answer".
     * Also publish the message to the EventBus.
     *
     * An old implementation would save failed sends from guests and maybe sent them later.
     *
     * @example
     * try {
     *   ...
     * } catch (err) {
     *   if (err.status == 401) {
     *     storage.set('message_not_sent', this.id);
     *   }
     *   throw reason;
     * }
     */
    async sendMessage(message: string, attachment?: Attachment) {
      const [amateur, lastMessage] = await Promise.all([
        Amateur.get(this.id), //
        this.getLastMessage().catch(() => null),
      ]);
      const res = await http().post(
        'api/message/send',
        {
          body: message,
          recipient: amateur.id,
          price: price.get(amateur),
          attachment_id: attachment?.id,
          icebreakerAnswer: !!lastMessage?.isIcebreaker(),
        },
        {
          headers: {'Content-Type': 'application/json'},
          signupOverlayParams: {
            reason: 'messenger.signupRequired', //
            params: {nickname: amateur.getNickname()},
          },
          paymentOverlayParams: {
            reason: 'messenger.paymentRequired', //
            params: {nickname: amateur.getNickname()},
          },
        }
      );
      if (lastMessage?.isIcebreaker()) {
        logger(
          new onsLaforgeEvents.IcebreakerResponse(
            lastMessage.body, //
            lastMessage.payload.ibid,
            this.id
          )
        );
      }
      const data: MessageData = {
        body: message,
        recipient: `${amateur.id}`,
        sender: `${user.id}`,
        isMine: true,
        time: res.data.time * 1000,
        payload: {},
        attachment: attachment
          ? {
              id: attachment.id,
              name: attachment.name,
              status: 'ok',
              url: attachment.url,
            }
          : null,
      };
      await sock.publish('message.sent', data);
    }
  };
}
